import React from 'react';
import { Box, ListItem, Chip, Grid, Typography } from '@mui/material';
import { NeynarCastCard } from '@neynar/react';
import { 
  ThumbUp, 
  Share, 
  Comment, 
  TrendingUp, 
  Whatshot,
  People,
  PersonAdd,
} from '@mui/icons-material';

// Component to render AI labels as badges
const AILabelsSection = ({ aiLabels }) => {
  if (!aiLabels || aiLabels.length === 0) return null;

  const getLabelColor = (confidence) => {
    switch (confidence) {
      case 'high':
        return 'secondary'; // Purple
      case 'med':
        return 'error'; // Red  
      case 'low':
        return 'warning'; //Orange
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption">
        AI Labels:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {aiLabels.map((label) => {
            return <Chip
              key={label.id}
              label={label.id}
              color={getLabelColor(label.confidence)}
              size="small"
              variant="outlined"
            />
        })}
      </Box>
    </Box>
  );
};

// Component to render engagement metrics with icons
const EngagementSection = ({ hit }) => {
  const { _source } = hit;
  const engagementMetrics = [
    { key: 'num_like', value: _source.num_like, icon: ThumbUp, label: 'Likes' },
    { key: 'num_share', value: _source.num_share, icon: Share, label: 'Shares' },
    { key: 'num_comment', value: _source.num_comment, icon: Comment, label: 'Comments' },
    { key: 'score_popular', value: _source.score_popular, icon: Whatshot, label: 'Popularity' },
    { key: 'score_trending', value: _source.score_trending, icon: TrendingUp, label: 'Trending' }
  ];
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
        Engagement:
      </Typography>
      <Grid container spacing={1}>
        {engagementMetrics.map((o) => (
          <Grid key={o.key}>
            <Box sx={{ 
              p: 1, 
              backgroundColor: 'grey.50', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 'fit-content'
            }}>
              <o.icon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {o.value!=null?o.value.toFixed(2):'-'}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Component to render spam and moderation metrics with icons
const SpamModerationSection = ({ hit }) => {
  const { _source } = hit;
  
  const spamModerationMetrics = [
    { key: 'score_spam', value: _source.score_spam, label: 'Spam' },
    { key: 'score_not_ok', value: _source.score_not_ok, label: 'Not-Ok' },
    { key: 'user_score_spam', value: _source.user_score_spam, label: 'User Spam' },
    { key: 'user_events_num', value: _source.user_events_num, label: 'User Num Events' },
    { key: 'user_events_per_day', value: _source.user_events_per_day, label: 'User Events/Day' },
    { key: 'user_warpcast_spam', value: _source.user_warpcast_spam, label: 'Warpcast Label' }
  ];

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
        Spam & Moderation:
      </Typography>
      <Grid container spacing={1}>
        {spamModerationMetrics.map((o) => (
          <Grid key={o.key}>
            <Box sx={{ 
              p: 1, 
              backgroundColor: 'grey.50', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 'fit-content'
            }}>
              <Typography variant="caption" color="text.secondary">
                {o.label}: {o.value != null ? o.value.toFixed(2) : '-'}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Component to render item ID and ES score
const ItemInfoSection = ({ hit, item_id }) => {
  const { _source } = hit;
  
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
        Document:
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        p: 1,
        backgroundColor: 'grey.50', 
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200'
      }}>
        <Typography variant="caption" color="text.secondary">
          {item_id}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={_source.app || '-'}
            size="small"
            variant="filled"
            color="secondary"
            sx={{ borderRadius: 0.5 }}
          />
          <Chip 
            label={_source.lang || '-'}
            size="small"
            variant="filled"
            color="warning"
            sx={{ borderRadius: 0.5 }}
          />
          <Chip 
            label={hit._score != null ? hit._score.toFixed(2) : '-'}
            size="small"
            variant="outlined"
            color="primary"
          />
        </Box>
      </Box>
    </Box>
  );
};

// Component to render other attributes
const OtherAttributesSection = ({ hit }) => {
  const { _source } = hit;
  
  const attributes = [
    'publication_type',
    'channels',
    'domains',
    'embed_users',
    'location',
    'miniapp_categories',
    'score_like',
    'score_share',
    'score_comment',
    'score_reaction',
    'cluster_id',
    'text_length',
    'video_orientations',
    'video_languages',
    'zora_address'
  ];

  const formatValue = (key, value) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    // Special handling for cluster_id (integer)
    if (key === 'cluster_id') {
      return value.toString();
    }
    
    // Handle numeric values (round to 2 decimals)
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    
    // Handle arrays and objects
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : '-';
    }
    
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    
    // Handle strings and other types as-is
    return value.toString();
  };

  const validAttributes = attributes.filter(key => _source.hasOwnProperty(key));
  
  if (validAttributes.length === 0) return null;

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
        Other Attributes:
      </Typography>
      <Grid container spacing={1}>
        {validAttributes.map((key) => (
          <Grid key={key}>
            <Box sx={{ 
              p: 1, 
              backgroundColor: 'grey.50', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {key}: {formatValue(key, _source[key])}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Component to render enriched text
const EnrichedTextSection = ({ hit }) => {
  const { _source } = hit;
  
  if (!_source.text_enriched || _source.text_enriched === '') {
    return null;
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
        Enriched text:
      </Typography>
      <Box sx={{ 
        p: 1, 
        backgroundColor: 'grey.50', 
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200'
      }}>
        <Typography variant="caption" color="text.secondary">
          {_source.text_enriched}
        </Typography>
      </Box>
    </Box>
  );
};

// Component to render author features
const AuthorFeaturesSection = ({ hit }) => {
  const { _source } = hit;
  const user_pfp = _source.user_pfp;
  const user_name = _source.user_name;
  const user_id = _source.user_id;
  const num_follower = _source.num_follower;
  const num_following = _source.num_following;

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
        Author:
      </Typography>
      <Box sx={{ 
        p: 1, 
        backgroundColor: 'grey.50', 
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        {user_pfp && (
          <Box
            component="img"
            src={user_pfp}
            alt={user_name || 'User avatar'}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              objectFit: 'cover',
              flexShrink: 0
            }}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: 32, gap: 0.25 }}>
          {user_id && (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, fontSize: '0.65rem' }}>
              #{user_id}
            </Typography>
          )}
          {user_name && (
            <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1 }}>
              @{user_name}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          {num_follower != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <People sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {num_follower.toFixed(0)}
              </Typography>
            </Box>
          )}
          {num_following != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <PersonAdd sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {num_following.toFixed(0)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const FarcasterItemCard = ({ hit }) => {
  const { _source } = hit;
  const item_id = _source.item_id;
  const aiLabels_high = _source.ai_labels_high || [];
  const aiLabels_med = _source.ai_labels_med || [];
  const aiLabels_low = _source.ai_labels_low || [];
  const aiLabelsMap = {}
  for (const label of aiLabels_high) {
    aiLabelsMap[label] = 'high';
  }
  for (const label of aiLabels_med) {
    if (!(label in aiLabelsMap)) {
      aiLabelsMap[label] = 'med';
    }
  }
  for (const label of aiLabels_low) {
    if (!(label in aiLabelsMap)) {
      aiLabelsMap[label] = 'low';
    }
  }
  const aiLabels = Object.keys(aiLabelsMap).map((label) => ({
    id: label,
    confidence: aiLabelsMap[label]
  }));
  
  return (
    <ListItem 
      sx={{ 
        borderBottom: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 2
      }}
    >
      <Box sx={{ mb: 2 }}>
        <NeynarCastCard
          type="hash"
          identifier={_source.item_id.slice(2)}
          allowReactions={false}
          renderEmbeds={true}
          renderFrames={false}
          onError={(error) => {
            //console.error('Error loading cast:', error);
          }}
        />
      </Box>
      <Box sx={{ pl: 1 }}>
        <ItemInfoSection hit={hit} item_id={item_id} />
        <AILabelsSection aiLabels={aiLabels} />
        <EngagementSection hit={hit} />
        <SpamModerationSection hit={hit} />
        <OtherAttributesSection hit={hit} />
        <EnrichedTextSection hit={hit} />
        <AuthorFeaturesSection hit={hit} />
      </Box>
    </ListItem>
  );
};

export default FarcasterItemCard;

